import { useState } from "react";
/* eslint-disable react-refresh/only-export-components -- the recipe types
   and ingredientAmount helper are shared with Cook; splitting them into a
   separate module helps nobody. */
// Recipe detail: hero image, title, go/no-go banner, then THE recipe — the
// Cooking for Engineers summary table (vault "KitchenOS - Idea" § Recipe
// display format). Ingredients with dual units run down the left column;
// merged cells to the right show which ingredients combine in which step,
// left to right in preparation order. One glanceable table, no prose.
//
// Recipes without a grid yet (imported before the generator, or a failed
// run) fall back to the ingredient/step lists and offer to build one.

import { Link, useNavigate, useParams } from "react-router-dom";
import useSWR from "swr";
import { ArrowLeft, Check, Clock, Sparkles, X } from "lucide-react";

import { fetcher, imageUrl, post } from "@/api/client";
import { cn } from "@/lib/utils";
import { macros, type Nutrition } from "@/lib/nutrition";

export interface RecipeIngredient {
  id: number;
  item_name: string;
  qty_metric: number | null;
  unit_metric: string | null;
  qty_display: string | null;
  optional: number;
  availability: "ok" | "missing";
}

export interface RecipeStep {
  id: number;
  position: number;
  action: string;
  minutes: number | null;
}

export interface GridIngredient {
  display: string;
  name: string;
  qty_metric: number | null;
  unit_metric: string | null;
}

export interface GridCell {
  label: string;
  from_row: number;
  to_row: number;
  minutes: number | null;
}

export interface RecipeGrid {
  preamble: string[];
  ingredients: GridIngredient[];
  columns: GridCell[][];
}

export type GridStatus = "none" | "pending" | "ready" | "failed";

export interface RecipeFull {
  id: number;
  name: string;
  servings: number;
  prep_min: number | null;
  cook_min: number | null;
  cuisine: string | null;
  image: string | null;
  description: string | null;
  grid: RecipeGrid | null;
  grid_status: GridStatus;
  grid_available: boolean;
  nutrition: Nutrition | null;
  ingredients: RecipeIngredient[];
  steps: RecipeStep[];
  cookable: "go" | "no_go";
  missing_count: number;
}

export function ingredientAmount(i: RecipeIngredient): string {
  if (i.qty_display) return i.qty_display;
  if (i.qty_metric) return `${i.qty_metric} ${i.unit_metric ?? ""}`.trim();
  return "";
}

export default function RecipeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [estimating, setEstimating] = useState(false);
  const {
    data: recipe,
    isLoading,
    mutate,
  } = useSWR<RecipeFull>(`/api/recipes/${id}`, fetcher, {
    // While Claude lays the grid out, poll until it lands.
    refreshInterval: (latest) => (latest?.grid_status === "pending" ? 3000 : 0),
  });

  if (isLoading || !recipe) {
    return (
      <div className="flex-1 p-6">
        <div className="epicure-skeleton h-64 rounded-[var(--radius-card)]" />
      </div>
    );
  }

  const total = (recipe.prep_min ?? 0) + (recipe.cook_min ?? 0);
  const hero = imageUrl(recipe.image);

  async function buildGrid() {
    await post(`/api/recipes/${recipe!.id}/grid`, {});
    void mutate();
  }

  async function estimateNutrition() {
    if (estimating) return;
    setEstimating(true);
    try {
      await post(`/api/recipes/${recipe!.id}/nutrition`, {});
      void mutate();
    } catch {
      // the recipe simply keeps showing no numbers
    } finally {
      setEstimating(false);
    }
  }

  return (
    <div className="min-w-0 flex-1 overflow-y-auto">
      <div className="mx-auto max-w-4xl p-4 md:p-6">
        <Link
          to="/browse"
          className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-line-strong px-3.5 py-1.5 text-xs font-semibold text-dim transition-colors hover:text-foreground"
        >
          <ArrowLeft className="size-3.5" /> library
        </Link>

        {/* hero */}
        <div className="relative overflow-hidden rounded-[var(--radius-card)] border border-line">
          {hero && <img src={hero} alt="" className="h-64 w-full object-cover md:h-80" />}
          <div
            className={cn(
              "flex flex-wrap items-end justify-between gap-3 p-5",
              hero && "absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent pt-16",
            )}
          >
            <div>
              <h1
                className={cn(
                  "font-display text-4xl leading-none font-medium tracking-[-0.02em] md:text-5xl",
                  hero ? "text-[#f5f2e8]" : "text-foreground",
                )}
              >
                {recipe.name}
              </h1>
              <div
                className={cn(
                  "mt-2 flex items-center gap-3 text-sm",
                  hero ? "text-[#f5f2e8]/75" : "text-dim",
                )}
              >
                {total > 0 && (
                  <span className="flex items-center gap-1">
                    <Clock className="size-3.5" /> {total} min
                  </span>
                )}
                {recipe.cuisine && <span>{recipe.cuisine}</span>}
                <span>{recipe.servings} servings</span>
              </div>
              {/* per serving: kcal + macros, or the offer to estimate them */}
              <div className={cn("mt-1.5 font-mono text-xs", hero ? "text-[#f5f2e8]/75" : "text-dim")}>
                {recipe.nutrition ? (
                  <span title={`per serving · ${recipe.nutrition.source === "llm" ? "estimated by Claude" : "house estimate"}`}>
                    {recipe.nutrition.kcal} kcal · {macros(recipe.nutrition)}
                    <span className="opacity-60"> / serving</span>
                  </span>
                ) : recipe.grid_available ? (
                  <button
                    type="button"
                    onClick={() => void estimateNutrition()}
                    disabled={estimating}
                    className="cursor-pointer underline decoration-dotted underline-offset-4 hover:opacity-100 disabled:opacity-50"
                  >
                    {estimating ? "estimating kcal & macros…" : "estimate kcal & macros"}
                  </button>
                ) : null}
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate(`/cook?recipe=${recipe.id}`)}
              className="cursor-pointer rounded-full bg-brand px-6 py-3 text-sm font-bold text-primary-foreground transition-[transform,background-color] duration-500 ease-epicure hover:-translate-y-0.5 hover:bg-[var(--brand-hover)] active:scale-[0.98]"
            >
              Cook this
            </button>
          </div>
        </div>

        {recipe.description && (
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-dim">{recipe.description}</p>
        )}

        {/* go / no-go */}
        <div
          className={cn(
            "mt-4 rounded-xl border px-4 py-3 text-sm",
            recipe.cookable === "go"
              ? "border-status-ok/40 bg-[var(--status-ok-tint)]"
              : "border-status-error/40 bg-[var(--status-error-tint)]",
          )}
        >
          {recipe.cookable === "go"
            ? "Everything essential is in stock — go."
            : `${recipe.missing_count} essential ingredient${recipe.missing_count === 1 ? " is" : "s are"} missing — check the list.`}
        </div>

        {recipe.grid ? (
          <section className="mt-6">
            <h2 className="mb-3 font-mono text-[11px] uppercase tracking-[0.14em] text-brand-ink">
              Recipe
            </h2>
            <GridTable grid={recipe.grid} stock={recipe.ingredients} />
            <p className="mt-2 text-xs text-dimmer">
              Read it left to right: each column is one step, spanning the ingredients it uses.
            </p>
          </section>
        ) : (
          <>
            <GridNotice status={recipe.grid_status} available={recipe.grid_available} onBuild={buildGrid} />
            <ListView recipe={recipe} />
          </>
        )}
      </div>
    </div>
  );
}

// ── The grid ──────────────────────────────────────────────────────────────

function norm(s: string): string {
  return s.toLowerCase().replace(/[^a-z ]/g, " ").replace(/\s+/g, " ").trim();
}

/** Stock check for a grid row: exact or containing match against the recipe's
 *  ingredient list (the grid's names come from the LLM and may be phrased
 *  slightly differently). Unknown → no mark rather than a wrong one. */
function stockFor(ing: GridIngredient, stock: RecipeIngredient[]): RecipeIngredient | undefined {
  const want = norm(ing.name);
  if (!want) return undefined;
  return (
    stock.find((s) => norm(s.item_name) === want) ??
    stock.find((s) => {
      const have = norm(s.item_name);
      return have.includes(want) || want.includes(have);
    })
  );
}

function GridTable({ grid, stock }: { grid: RecipeGrid; stock: RecipeIngredient[] }) {
  const cols = grid.columns;
  const span = 1 + cols.length;

  // Per column: which row starts a cell, and which rows a cell already covers.
  const starts = cols.map((col) => new Map(col.map((c) => [c.from_row, c] as const)));
  const covered = cols.map((col) => {
    const rows = new Set<number>();
    for (const c of col) for (let r = c.from_row; r <= c.to_row; r++) rows.add(r);
    return rows;
  });

  const cell = "border border-line-strong px-2.5 py-2 align-middle";

  return (
    <div className="-mx-4 overflow-x-auto px-4 md:mx-0 md:px-0">
      <table className="w-full min-w-[520px] table-fixed border-collapse text-sm">
        <colgroup>
          <col className="w-[46%]" />
          {cols.map((_, c) => (
            <col key={c} />
          ))}
        </colgroup>
        <tbody>
          {grid.preamble.map((line, i) => (
            <tr key={`pre-${i}`}>
              <td colSpan={span} className={cn(cell, "text-center italic text-dim")}>
                {line}
              </td>
            </tr>
          ))}
          {grid.ingredients.map((ing, r) => {
            const s = stockFor(ing, stock);
            return (
              <tr key={r}>
                <td className={cn(cell, "bg-surface leading-snug")}>
                  <span className="flex items-start gap-2">
                    {s &&
                      (s.availability === "ok" ? (
                        <Check className="mt-0.5 size-3.5 shrink-0 text-status-ok" />
                      ) : (
                        <X
                          className={cn(
                            "mt-0.5 size-3.5 shrink-0",
                            s.optional ? "text-dimmer" : "text-status-error",
                          )}
                        />
                      ))}
                    <span>{ing.display}</span>
                  </span>
                </td>
                {cols.map((_, c) => {
                  const step = starts[c].get(r);
                  if (step) {
                    const last = c === cols.length - 1;
                    return (
                      <td
                        key={c}
                        rowSpan={step.to_row - step.from_row + 1}
                        className={cn(
                          cell,
                          "text-center font-semibold text-brand-ink",
                          last ? "bg-[var(--brand-tint-12)]" : "bg-[var(--brand-tint-08)]",
                        )}
                      >
                        <span className="block text-balance leading-snug break-words">{step.label}</span>
                        {step.minutes != null && (
                          <span className="mt-1 flex items-center justify-center gap-1 font-mono text-[11px] font-normal text-dim">
                            <Clock className="size-3" /> {step.minutes} min
                          </span>
                        )}
                      </td>
                    );
                  }
                  if (covered[c].has(r)) return null;
                  return <td key={c} className={cell} />;
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Fallbacks ─────────────────────────────────────────────────────────────

function GridNotice({
  status,
  available,
  onBuild,
}: {
  status: GridStatus;
  available: boolean;
  onBuild: () => Promise<void>;
}) {
  if (status === "pending") {
    return (
      <p className="mt-4 flex items-center gap-2 text-sm text-brand-ink">
        <Sparkles className="size-3.5 animate-pulse" /> laying the recipe out as a grid…
      </p>
    );
  }
  if (!available) {
    return (
      <p className="mt-4 text-xs text-dimmer">
        No grid for this recipe — grid layout needs an Anthropic API key on the server.
      </p>
    );
  }
  return (
    <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-dim">
      <span>{status === "failed" ? "The grid layout failed last time." : "No grid yet."}</span>
      <button
        type="button"
        onClick={() => void onBuild()}
        className="cursor-pointer rounded-full border border-line-strong px-3.5 py-1.5 text-xs font-semibold text-brand-ink transition-colors hover:border-brand"
      >
        {status === "failed" ? "try again" : "build the grid"}
      </button>
    </div>
  );
}

function ListView({ recipe }: { recipe: RecipeFull }) {
  return (
    <div className="mt-6 grid gap-8 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
      <section>
        <h2 className="mb-3 font-mono text-[11px] uppercase tracking-[0.14em] text-brand-ink">
          Ingredients
        </h2>
        <ul className="flex flex-col divide-y divide-line">
          {recipe.ingredients.map((i) => (
            <li key={i.id} className="flex items-center gap-2.5 py-2 text-sm">
              {i.availability === "ok" ? (
                <Check className="size-4 shrink-0 text-status-ok" />
              ) : (
                <X className={cn("size-4 shrink-0", i.optional ? "text-dimmer" : "text-status-error")} />
              )}
              <span className={cn("flex-1", i.optional && "text-dim")}>
                {i.item_name}
                {i.optional ? " (optional)" : ""}
              </span>
              <span className="font-mono text-xs text-dim">{ingredientAmount(i)}</span>
            </li>
          ))}
        </ul>
      </section>

      <section>
        <h2 className="mb-3 font-mono text-[11px] uppercase tracking-[0.14em] text-brand-ink">
          Steps
        </h2>
        <ol className="flex flex-col gap-4">
          {recipe.steps.map((s) => (
            <li key={s.id} className="flex gap-3.5">
              <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-panel font-display text-base font-semibold text-brand-ink">
                {s.position}
              </span>
              <p className="text-sm leading-relaxed">{s.action}</p>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}
